import React from 'react'
import styles from './UIGameLayout.module.scss'
import {UIGameEnd} from '../UIGameEnd'
import {UIGameFeeds} from '../UIGameFeeds/UIGameFeeds'
import {UIGamePause} from '../UIGamePause/UIGamePause'
import {UIGameUserSettings} from '../UIGameUserSettings'
import {UIGameHelpButtons, UIGameHelpUserSettingsButtonContainer} from '../UIGameHelpButtons/UIGameHelpButtons'
import {StorageWrapper} from '../../utils/storage'
import {UIGameSpectators} from '../UIGameSpectators/UIGameSpectators'
import {UIGameHelpOpenChatLogsButton} from '../UIGameHelpButtons/UIGameHelpChatLogsButton/UIGameHelpChatLogsButton'
import {useUIGameReplayContext} from '../../contexts/UIGameReplayContext'
import {UIGameCardAnimator} from '../UIGameCardAnimator/UIGameCardAnimator'
import {UIGameCardAnimatorProvider} from '../UIGameCardAnimator/UIGameCardAnimatorContext'
import {UIGameVoteContainer} from '../UIGameVote/UIGameVoteContainer'
import {useUIGameUserSettingsContext} from '../../contexts/UIGameUserSettingsContext'

type UIGameOrientation = 'portrait' | 'landscape'

interface UIGameLayoutProps {
    orientation: UIGameOrientation
    canvasWidth: number
    canvasHeight: number
    gameFeedsWidth: number
    gameFeedsHeight: number
    bottomHeight: number
    /**
     * @deprecated Define sizes using responsive CSS instead. Remove once UIGameCardInventory is converted to ReactJS
     */
    itemSize: number
    isChatDefaultMinimized: boolean
    isChatEnabled: boolean
    onClickLog: (logIndex: number) => void
    onSendChat: (message: string) => void
    onSetChatVisible: (visible: boolean) => void
    onSetLogVisible: (visible: boolean) => void
    tempMobileIsEndScreenVisible: boolean
    initialCanResign: boolean
}

export const UIGameLayout = (props: UIGameLayoutProps) => {
    if(props.orientation === 'portrait') return <UIGameLayoutPortrait {...props} />
    else return <UIGameLayoutLandscape {...props} />
}

export const UIGameLayoutLandscape = ({
    canvasWidth,
    canvasHeight,
    gameFeedsWidth,
    gameFeedsHeight,
    itemSize,
    isChatDefaultMinimized,
    isChatEnabled,
    onClickLog,
    onSendChat,
    initialCanResign,
}: UIGameLayoutProps) => {
    const {isAnimationsEnabled} = useUIGameUserSettingsContext()

    return (
        <div
            className={styles.containerLandscape}
            style={{
                gridTemplateColumns: `[start] auto [help-buttons-end] 1fr [game-feeds] ${gameFeedsWidth}px [end]`,
                gridTemplateRows: `[start] ${gameFeedsHeight}px [game-feeds-end] 1fr [end]`}}>
            <div className={styles.gameHelpButtonsContainer}>
                <UIGameHelpButtons initialCanResign={initialCanResign}/>
            </div>
            <div className={styles.gameSpectatorsContainer}>
                <UIGameSpectators />
            </div>
            <div className={styles.gameSettingsContainer}>
                <UIGameUserSettings />
            </div>
            <div className={styles.gameEndContainer}>
                <UIGameEnd confettiWidth={canvasWidth - gameFeedsWidth} confettiHeight={canvasHeight} />
            </div>
            <div className={styles.gamePauseContainer}>
                <UIGamePause />
            </div>
            <UIGameFeeds
                isChatDefaultMinimized={isChatDefaultMinimized}
                isChatEnabled={isChatEnabled}
                onClickLog={onClickLog}
                onSendChat={onSendChat}
                className={styles.gameFeedsContainer}/>
            <div className={styles.gameCardAnimatorContainer}>
                <UIGameCardAnimatorProvider itemSize={itemSize} isAnimationsEnabled={isAnimationsEnabled}>
                    <UIGameCardAnimator />
                </UIGameCardAnimatorProvider>
            </div>
        </div>
    )
}

export const UIGameLayoutPortrait = ({
    bottomHeight,
    itemSize,
    onSetChatVisible,
    onSetLogVisible,
    tempMobileIsEndScreenVisible,
    initialCanResign,
}: UIGameLayoutProps) => {
    const safeAreaTop = StorageWrapper.getStatusBarHeight()
    const {isReplay} = useUIGameReplayContext()
    const {isAnimationsEnabled} = useUIGameUserSettingsContext()

    return (
        <div
            className={styles.containerPortrait}
            style={{
                gridTemplateColumns: `[start] 40px [help-buttons-end] 1fr [end]`,
                gridTemplateRows: `
                    [start]
                    ${safeAreaTop}px
                    [safe-area-top-end]
                    6.66%
                    [help-buttons-end]
                    1fr
                    [bottom-start]
                    ${bottomHeight}px
                    [end]`}}>
            {isReplay === false && tempMobileIsEndScreenVisible === false && <div className={styles.gameHelpUserSettingsButtonContainer}>
                <UIGameHelpUserSettingsButtonContainer
                    initialCanResign={initialCanResign}
                    isRulesOptionVisible={true}
                    isGameInfoOptionVisible={true} />
            </div>}
            {tempMobileIsEndScreenVisible === false && <div className={styles.gameSettingsContainer}>
                <UIGameUserSettings
                    isNestedContentEnabled={true}
                    // eslint-disable-next-line i18next/no-literal-string
                    enabledTabs={['game-info', 'display', 'audio']} />
            </div>}
            {tempMobileIsEndScreenVisible === false && <div className={styles.gamePauseContainer}>
                <UIGamePause />
            </div>}
            {tempMobileIsEndScreenVisible === false && <div className={styles.gameHelpOpenChatLogsButtonContainer}>
                <UIGameHelpOpenChatLogsButton
                    onSetChatVisible={onSetChatVisible}
                    onSetLogVisible={onSetLogVisible} />
            </div>}
            <div className={styles.gameCardAnimatorContainer}>
                <UIGameCardAnimatorProvider itemSize={itemSize} isAnimationsEnabled={isAnimationsEnabled}>
                    <UIGameCardAnimator />
                </UIGameCardAnimatorProvider>
            </div>
            {tempMobileIsEndScreenVisible === false && <div className={styles.gameVoteContainer}>
                <UIGameVoteContainer />
            </div>}
        </div>
    )
}
